import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { CheckCircle, XCircle, ArrowLeft, RotateCcw, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

export default function QuizView() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);

  useEffect(() => {
    fetch(`${API}/quizzes/${quizId}`, { credentials: "include" })
      .then(r => { if (r.ok) return r.json(); throw new Error(); })
      .then(setQuiz)
      .catch(() => toast.error("Error cargando el cuestionario"))
      .finally(() => setLoading(false));
  }, [quizId]);

  const handleAnswer = (questionId, optionIdx) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionIdx }));
  };

  const handleSubmit = async () => {
    if (!quiz) return;
    const answersArr = quiz.questions.map(q => ({
      question_id: q.question_id,
      selected_option: answers[q.question_id] ?? -1,
    }));
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/quizzes/${quizId}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers: answersArr }),
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data);
        toast.success(`Resultado: ${data.score}%`);
      }
    } catch {
      toast.error("Error enviando respuestas");
    }
    setSubmitting(false);
  };

  const handleRetry = () => {
    setAnswers({});
    setResults(null);
    setCurrentQ(0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-4 border-[#20D3C0] border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  if (results) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="quiz-results">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-12">
          <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_8px_30px_rgba(25,59,110,0.08)] text-center">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${results.score >= 70 ? 'bg-green-100' : 'bg-red-100'}`}>
              {results.score >= 70 ? (
                <Trophy className="w-10 h-10 text-green-500" />
              ) : (
                <XCircle className="w-10 h-10 text-red-500" />
              )}
            </div>
            <h2 className="text-3xl font-bold text-[#193B6E] mb-2" style={{ fontFamily: 'Outfit' }} data-testid="quiz-score">
              {results.score}%
            </h2>
            <p className="text-slate-500 mb-6">
              {results.correct} de {results.total} respuestas correctas
            </p>

            {/* Detailed results */}
            <div className="space-y-3 text-left mb-8">
              {results.results.map((r, i) => {
                const question = quiz.questions.find(q => q.question_id === r.question_id);
                return (
                  <div key={r.question_id} className={`p-4 rounded-xl border ${r.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-start gap-3">
                      {r.is_correct ? (
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-[#193B6E]">{question?.question}</p>
                        <p className="text-xs mt-1 text-slate-500">
                          Tu respuesta: {question?.options[r.selected] || "Sin respuesta"}
                          {!r.is_correct && <span className="text-green-600 ml-2">Correcta: {question?.options[r.correct]}</span>}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3 justify-center">
              <button
                onClick={handleRetry}
                className="bg-[#20D3C0] text-[#112C54] hover:bg-[#1AB8A6] font-semibold rounded-full px-6 py-3 transition-all flex items-center gap-2"
                data-testid="retry-quiz-btn"
              >
                <RotateCcw className="w-4 h-4" /> Intentar de nuevo
              </button>
              <button
                onClick={() => navigate(-1)}
                className="border-2 border-[#193B6E] text-[#193B6E] hover:bg-[#193B6E] hover:text-white font-semibold rounded-full px-6 py-3 transition-all"
                data-testid="back-to-lesson-btn"
              >
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const questions = quiz?.questions || [];
  const q = questions[currentQ];
  const allAnswered = questions.every(q => answers[q.question_id] !== undefined);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="quiz-view">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#193B6E] hover:text-[#20D3C0] mb-6 text-sm font-medium" data-testid="quiz-back">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>

        <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_8px_30px_rgba(25,59,110,0.08)]">
          <h2 className="text-xl font-bold text-[#193B6E] mb-2" style={{ fontFamily: 'Outfit' }} data-testid="quiz-title">
            {quiz?.title}
          </h2>

          {/* Progress */}
          <div className="flex items-center gap-3 mb-8">
            <Progress value={((currentQ + 1) / questions.length) * 100} className="h-2 flex-1" />
            <span className="text-sm text-slate-500 whitespace-nowrap">{currentQ + 1}/{questions.length}</span>
          </div>

          {/* Question */}
          {q && (
            <div className="animate-fade-in" key={q.question_id}>
              <h3 className="text-lg font-semibold text-[#193B6E] mb-6" data-testid="quiz-question">{q.question}</h3>
              <div className="space-y-3">
                {q.options.map((opt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(q.question_id, idx)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                      answers[q.question_id] === idx
                        ? 'border-[#20D3C0] bg-[#20D3C0]/5 text-[#193B6E]'
                        : 'border-slate-200 hover:border-[#20D3C0]/50 text-slate-600'
                    }`}
                    data-testid={`quiz-option-${idx}`}
                  >
                    <span className="font-medium">{String.fromCharCode(65 + idx)}.</span> {opt}
                  </button>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8">
                <button
                  onClick={() => setCurrentQ(prev => Math.max(0, prev - 1))}
                  disabled={currentQ === 0}
                  className="text-[#193B6E] font-medium text-sm disabled:opacity-30 flex items-center gap-1"
                  data-testid="quiz-prev"
                >
                  <ArrowLeft className="w-4 h-4" /> Anterior
                </button>
                {currentQ < questions.length - 1 ? (
                  <button
                    onClick={() => setCurrentQ(prev => Math.min(questions.length - 1, prev + 1))}
                    className="bg-[#193B6E] text-white font-medium rounded-full px-6 py-2 text-sm hover:bg-[#132D55] transition-all flex items-center gap-1"
                    data-testid="quiz-next"
                  >
                    Siguiente
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={!allAnswered || submitting}
                    className="bg-[#20D3C0] text-[#112C54] font-semibold rounded-full px-6 py-2 text-sm hover:bg-[#1AB8A6] transition-all disabled:opacity-50"
                    data-testid="quiz-submit"
                  >
                    {submitting ? "Enviando..." : "Enviar respuestas"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
